/**
 * Available date format profiles
 * Each profile defines a complete set of formats (date, time, datetime)
 */
export enum DateFormatProfile {
  ENGLISH = 'english', // Numeric formats: mm/dd/yyyy
  EUROPEAN = 'european', // Numeric formats: dd/mm/yyyy
}

/**
 * Format structure by type
 */
export interface DateFormatConfig {
  date: string;
  time: string;
  datetime: string;
  date_backend: string;
  datetime_backend: string;
}

/**
 * Mapping of profiles to format configurations (date-fns format)
 * These formats are used throughout the application for formatting dates
 */
export const DATE_FORMAT_PROFILES: Record<DateFormatProfile, DateFormatConfig> = {
  [DateFormatProfile.ENGLISH]: {
    date: 'MMM dd, yyyy',
    time: 'HH:mm',
    datetime: 'MMM dd, yyyy HH:mm',
    date_backend: 'dd-MM-yyyy',
    datetime_backend: 'dd-MM-yyyy HH:mm',
  },
  [DateFormatProfile.EUROPEAN]: {
    date: 'dd MMM, yyyy',
    time: 'HH:mm',
    datetime: 'dd MMM, yyyy HH:mm',
    date_backend: 'dd-MM-yyyy',
    datetime_backend: 'dd-MM-yyyy HH:mm',
  },
};

/**
 * Default profile
 */
export const DEFAULT_DATE_FORMAT_PROFILE = DateFormatProfile.EUROPEAN;

/**
 * Type for format profile (can be null)
 */
export type DateFormatProfileType = DateFormatProfile | null;

/**
 * Gets the format configuration for a profile
 */
export const getDateFormatConfig = (profile: DateFormatProfileType): DateFormatConfig => {
  if (!profile) {
    return DATE_FORMAT_PROFILES[DEFAULT_DATE_FORMAT_PROFILE];
  }
  return DATE_FORMAT_PROFILES[profile] || DATE_FORMAT_PROFILES[DEFAULT_DATE_FORMAT_PROFILE];
};

/**
 * Helper to get the formatted date example according to the profile
 */
export const getDateFormatExample = (profile: DateFormatProfileType): string => {
  switch (profile) {
    case DateFormatProfile.ENGLISH:
      return '12/25/2024';
    case DateFormatProfile.EUROPEAN:
    default:
      return '25/12/2024';
  }
};

/**
 * Type for date format profile label keys used in translations
 */
export type DateFormatProfileLabelKey = 'european' | 'english';

/**
 * Array with all available profiles (for UI)
 * Only European and English are shown in the configuration
 */
export const ALLOWED_DATE_FORMAT_PROFILES: Array<{
  key: DateFormatProfile;
  labelKey: DateFormatProfileLabelKey;
  example: string;
  description: string;
  dateFormat: string;
  timeFormat: string;
  datetimeFormat: string;
}> = [
  {
    key: DateFormatProfile.EUROPEAN,
    labelKey: 'european',
    example: '25 Dic 2024',
    description: 'dd MMM, yyyy',
    dateFormat: 'dd MMM, yyyy',
    timeFormat: 'HH:mm',
    datetimeFormat: 'dd MMM, yyyy HH:mm',
  },
  {
    key: DateFormatProfile.ENGLISH,
    labelKey: 'english',
    example: 'Dic 25, 2024',
    description: 'MMM dd, yyyy',
    dateFormat: 'MMM dd, yyyy',
    timeFormat: 'HH:mm',
    datetimeFormat: 'MMM dd, yyyy HH:mm',
  },
];

/**
 * @deprecated Use DateFormatProfile instead
 * Kept for backward compatibility during migration
 */
export enum DateFormatPreference {
  DD_MM_YYYY = 'dd/mm/YYYY',
  MM_DD_YYYY = 'mm/dd/YYYY',
}

/**
 * @deprecated Use DEFAULT_DATE_FORMAT_PROFILE instead
 */
export const DEFAULT_DATE_FORMAT = DateFormatPreference.DD_MM_YYYY;

/**
 * @deprecated Use ALLOWED_DATE_FORMAT_PROFILES instead
 */
export const ALLOWED_DATE_FORMATS: DateFormatPreference[] = [
  DateFormatPreference.DD_MM_YYYY,
  DateFormatPreference.MM_DD_YYYY,
];

/**
 * @deprecated Use DateFormatProfileType instead
 */
export type DateFormatPreferenceType = DateFormatPreference | null;
